import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SiteFooter from '../../components/feature/SiteFooter';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import { softwareTools } from '../../mocks/software-tools';
import { financeTools } from '../../mocks/finance-tools';
import { ecommerceTools } from '../../mocks/ecommerce-tools';
import { educationTools } from '../../mocks/education-tools';
import { healthTools } from '../../mocks/health-tools';
import { beautyTools } from '../../mocks/beauty-tools';
import { homeGardenTools } from '../../mocks/home-garden-tools';
import { gamingTools } from '../../mocks/gaming-tools';
import { travelTools } from '../../mocks/travel-tools';
import { familyPetTools } from '../../mocks/family-pet-tools';
import { hostingTools } from '../../mocks/hosting-tools';
import { foodBeverageTools } from '../../mocks/food-beverage-tools';
import { automotiveTools } from '../../mocks/automotive-tools';
import { realEstateTools } from '../../mocks/real-estate-tools';
import { useTranslation } from 'react-i18next';

interface SearchResult {
  slug: string;
  name: string;
  tagline: string;
  logoUrl?: string;
  logoIcon: string;
  logoGrad: string;
  rating: number;
  reviewCount: number;
  categoryLabel: string;
  categorySlug: string;
  categoryGrad: string;
  link: string;
}

const categoryMap = [
  { tools: softwareTools as any[], label: 'Software – SaaS – AI', slug: 'software-saas-ai', grad: 'from-violet-500 to-purple-600', icon: 'ri-robot-2-line' },
  { tools: financeTools as any[], label: 'Finance – Investment', slug: 'finance-investment', grad: 'from-emerald-500 to-teal-600', icon: 'ri-line-chart-line' },
  { tools: ecommerceTools as any[], label: 'Ecommerce & Shopping', slug: 'ecommerce-shopping', grad: 'from-orange-400 to-amber-500', icon: 'ri-shopping-cart-2-line' },
  { tools: educationTools as any[], label: 'Education & Learning', slug: 'education-learning', grad: 'from-sky-500 to-blue-600', icon: 'ri-graduation-cap-line' },
  { tools: healthTools as any[], label: 'Health – Fitness – Wellness', slug: 'health-fitness-wellness', grad: 'from-pink-500 to-rose-600', icon: 'ri-heart-pulse-line' },
  { tools: beautyTools as any[], label: 'Beauty – Fashion – Lifestyle', slug: 'beauty-fashion-lifestyle', grad: 'from-fuchsia-500 to-pink-500', icon: 'ri-sparkling-2-line' },
  { tools: homeGardenTools as any[], label: 'Home – Garden – DIY', slug: 'home-garden-diy', grad: 'from-lime-500 to-green-600', icon: 'ri-home-smile-line' },
  { tools: gamingTools as any[], label: 'Gaming – Entertainment', slug: 'gaming-entertainment', grad: 'from-purple-600 to-indigo-700', icon: 'ri-gamepad-line' },
  { tools: travelTools as any[], label: 'Travel – Lifestyle', slug: 'travel-lifestyle', grad: 'from-teal-400 to-cyan-500', icon: 'ri-plane-line' },
  { tools: familyPetTools as any[], label: 'Family – Pet – Kids', slug: 'family-pet-kids', grad: 'from-yellow-400 to-orange-400', icon: 'ri-group-line' },
  { tools: hostingTools as any[], label: 'Hosting Platforms', slug: 'hosting-platforms', grad: 'from-violet-600 to-purple-700', icon: 'ri-server-line' },
  { tools: foodBeverageTools as any[], label: 'Food & Beverage', slug: 'food-beverage', grad: 'from-teal-500 to-emerald-500', icon: 'ri-restaurant-2-line' },
  { tools: automotiveTools as any[], label: 'Automotive & Transportation', slug: 'automotive-transportation', grad: 'from-slate-500 to-gray-600', icon: 'ri-car-line' },
  { tools: realEstateTools as any[], label: 'Real Estate & Property', slug: 'real-estate-property', grad: 'from-fuchsia-400 to-rose-500', icon: 'ri-building-2-line' },
];

function buildAllResults(): SearchResult[] {
  const results: SearchResult[] = [];
  for (const cat of categoryMap) {
    for (const tool of cat.tools) {
      results.push({
        slug: tool.slug,
        name: tool.name,
        tagline: tool.tagline || tool.desc || '',
        logoUrl: tool.logoUrl,
        logoIcon: tool.logoIcon || 'ri-tools-line',
        logoGrad: tool.logoGrad || 'from-gray-400 to-gray-500',
        rating: tool.rating ?? 0,
        reviewCount: tool.reviewCount ?? tool.reviews ?? 0,
        categoryLabel: cat.label,
        categorySlug: cat.slug,
        categoryGrad: cat.grad,
        link: tool.link || '#',
      });
    }
  }
  return results;
}

const allResults = buildAllResults();

export default function SearchPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get('q') ?? '');
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = searchParams.get('q') ?? '';
  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const searchSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: '247digitrend',
        url: siteUrl,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'SearchResultsPage',
        '@id': `${siteUrl}/search`,
        name: query ? `Search: "${query}" – 247digitrend` : 'Search Tools – 247digitrend',
        url: `${siteUrl}/search`,
        description: 'Search across 1,200+ digital tools, reviews and comparisons on 247digitrend.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Search', item: `${siteUrl}/search` },
          ],
        },
      },
    ],
  };

  const filtered = query.trim().length === 0
    ? []
    : allResults.filter((r) => {
        const q = query.toLowerCase();
        return (
          r.name.toLowerCase().includes(q) ||
          r.tagline.toLowerCase().includes(q) ||
          r.categoryLabel.toLowerCase().includes(q)
        );
      });

  useEffect(() => {
    setInputValue(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={query ? `Search: "${query}" – 247digitrend` : 'Search Tools – 247digitrend'}
        description="Search across 1,200+ digital tools, reviews and comparisons on 247digitrend."
        canonical={`${siteUrl}/search`}
        keywords="search tools, find software, compare platforms, digital tool search"
        robots="noindex, follow"
      />
      <JsonLd data={searchSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white border-b border-gray-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247compare" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── SEARCH HERO ── */}
      <section className="pt-44 pb-10" style={{ background: 'linear-gradient(160deg, #f5f3ff 0%, #fdf4ff 40%, #fce7f3 100%)' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-1.5 text-xs text-violet-600 font-semibold mb-4 bg-violet-50 border border-violet-100 rounded-full px-4 py-1.5">
            <i className="ri-search-line"></i>
            1,200+ tools available
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-3 tracking-tight">
            Find the{' '}
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #7c3aed, #a21caf, #db2777)' }}>
              perfect tool
            </span>
          </h1>
          <p className="text-sm text-gray-500 mb-8">Type a tool name, category or keyword to search across all projects</p>

          {/* Search bar */}
          <div className="flex items-center bg-white rounded-full border border-gray-200 overflow-hidden px-1" style={{ boxShadow: '0 4px 24px rgba(124,58,237,0.12)' }}>
            <i className="ri-search-line text-gray-400 text-base ml-4"></i>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search tools, reviews, comparisons..."
              className="flex-1 px-4 py-4 text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
              autoFocus
            />
            <button
              onClick={handleSearch}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity m-0.5"
            >
              Search
            </button>
          </div>
        </div>
      </section>

      {/* ── RESULTS ── */}
      <section className="py-10 bg-white min-h-[400px]">
        <div className="max-w-5xl mx-auto px-8">

          {/* No query yet */}
          {query.trim() === '' && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-violet-50">
                <i className="ri-search-2-line text-3xl text-violet-400"></i>
              </div>
              <p className="text-base font-bold text-gray-700 mb-1">Start typing to search</p>
              <p className="text-sm text-gray-400">Enter a project or category name above</p>
            </div>
          )}

          {/* Has query but no results */}
          {query.trim() !== '' && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                <i className="ri-search-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-base font-bold text-gray-700 mb-1">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-sm text-gray-400 mt-1">Try a different keyword or browse the categories</p>
              <a href="/categories" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-violet-600 bg-violet-50 hover:bg-violet-100 transition-colors rounded-full px-5 py-2.5 cursor-pointer">
                <i className="ri-grid-line"></i> Browse all categories
              </a>
            </div>
          )}

          {/* Results found */}
          {filtered.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-black text-gray-900">
                    {filtered.length} result{filtered.length > 1 ? 's' : ''} for &ldquo;<span className="text-violet-600">{query}</span>&rdquo;
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">Searched across 14 categories</p>
                </div>
                <a href="/categories" className="text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors flex items-center gap-1 cursor-pointer">
                  Browse all <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((result) => (
                  <a
                    key={`${result.categorySlug}-${result.slug}`}
                    href={`/categories/${result.categorySlug}/${result.slug}`}
                    className="group bg-white rounded-2xl border border-gray-100 p-5 hover:border-violet-200 hover:-translate-y-0.5 transition-all cursor-pointer block"
                    style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.05)' }}
                  >
                    {/* Logo + category badge */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {result.logoUrl ? (
                          <div className="w-11 h-11 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-white flex items-center justify-center">
                            <img src={result.logoUrl} alt={result.name} className="w-full h-full object-contain" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          </div>
                        ) : (
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${result.logoGrad} flex-shrink-0`}>
                            <i className={`${result.logoIcon} text-white text-base`}></i>
                          </div>
                        )}
                        <div>
                          <h3 className="text-sm font-black text-gray-900 leading-tight group-hover:text-violet-700 transition-colors">{result.name}</h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            {renderStars(result.rating)}
                            <span className="text-xs text-gray-400 ml-0.5">{result.rating.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tagline */}
                    <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{result.tagline}</p>

                    {/* Footer: category + arrow */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gradient-to-r ${result.categoryGrad} text-white`}>
                        {result.categoryLabel}
                      </span>
                      <span className="text-xs text-violet-500 font-semibold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform whitespace-nowrap">
                        View <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <SiteFooter />
      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />

      {/* Sign Up Modal */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowSignUp(false)}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowSignUp(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            <div className="text-center mb-6">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                <i className="ri-bar-chart-grouped-line text-white text-base"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
              <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
            </div>
            <p className="text-center text-sm text-gray-500">Sign up to save your favorite tools and get personalized recommendations.</p>
            <button onClick={() => setShowSignUp(false)} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
